import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Stack,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Rating,
  Badge,
  Pagination,
  Skeleton
} from '@mui/material';
import {
  Search,
  LocationOn,
  Star,
  Store,
  Phone,
  Email,
  Schedule,
  Verified
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import boutiqueService from '../services/boutiqueService';
import ImageCard from '../components/ImageCard';

const Boutiques = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(21);

  const categories = [
    'Toutes'
  ];

  // Charger les boutiques depuis l'API
  useEffect(() => {
    const loadBoutiques = async () => {
      try {
        setLoading(true);
        // Récupérer toutes les boutiques avec une limite élevée
        const data = await boutiqueService.getBoutiques({ limit: 10000, page: 1 });
        // console.log('Boutiques reçues:', data);
        
        // La réponse peut être { boutiques: [...] } ou directement un tableau
        const boutiquesList = data.boutiques || (Array.isArray(data) ? data : []);
        // console.log('Nombre de boutiques:', boutiquesList.length);
        
        setBoutiques(boutiquesList);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des boutiques:', err);
        setError('Erreur lors du chargement des boutiques');
        setBoutiques([]);
      } finally {
        setLoading(false);
      }
    };

    loadBoutiques();
  }, []);

  const filteredBoutiques = boutiques.filter(boutique => {
    const matchesSearch = boutique.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (boutique.description && boutique.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === '' || selectedCategory === 'Toutes' || boutique.specialties?.includes(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  // Calcul de la pagination
  const totalPages = Math.ceil(filteredBoutiques.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBoutiques = filteredBoutiques.slice(startIndex, endIndex);

  // Fonction pour changer de page
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    // Scroll vers le haut de la page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset de la page quand on change de recherche ou catégorie
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const BoutiqueSkeleton = () => (
    <Card>
      <Skeleton variant="rectangular" height={200} />
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" height={32} />
            <Skeleton variant="text" height={20} width="60%" />
          </Box>
        </Box>
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} width="80%" />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 }  }}>
      {/* Header with Search and Filters */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
          mb: 2
        }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Nos boutiques partenaires
            </Typography>
            {/* <Typography variant="h6" color="text.secondary">
              Découvrez nos boutiques partenaires et leurs produits exclusifs
            </Typography> */}
          </Box>
          
          {/* Search and Filters */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems="center"
            sx={{ 
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: 400 }
            }}
          >
            <TextField
              placeholder="Rechercher une boutique..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
              sx={{ 
                flexGrow: 1, 
                maxWidth: { xs: '100%', sm: 400 },
                width: { xs: '100%', sm: 'auto' }
              }}
            />
            {/* <Stack direction="row" spacing={1} flexWrap="wrap">
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack> */}
          </Stack>
        </Box>
      </Box>

      {/* Results */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {filteredBoutiques.length} boutique{filteredBoutiques.length > 1 ? 's' : ''} trouvée{filteredBoutiques.length > 1 ? 's' : ''}
          {boutiques.length !== filteredBoutiques.length && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              (sur {boutiques.length} au total)
            </Typography>
          )}
          {totalPages > 1 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              (Page {currentPage} sur {totalPages})
            </Typography>
          )}
        </Typography>
        {/* <Stack direction="row" spacing={1}>
          <Chip label="Vérifiées" color="success" variant="outlined" />
          <Chip label="Nouveautés" variant="outlined" />
        </Stack> */}
      </Box>

      {/* Boutiques Grid */}
      {loading ? (
        <Grid container spacing={3} sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gridAutoRows: '1fr'
        }}>
          {[...Array(9)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <BoutiqueSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}   sx={{display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gridAutoRows: '1fr'}}>
          {currentBoutiques.map((boutique) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={boutique.id} >
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <ImageCard
                  src={boutique.logo}
                  alt={boutique.name}
                  height={200}
                />
                {/* <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    display: 'flex',
                    gap: 1
                  }}
                >
                  {boutique.isActive && (
                    <Chip label="Active" color="primary" size="small" />
                  )}
                  {!boutique.isSuspended && (
                    <Chip 
                      icon={<Verified />} 
                      label="Vérifiée" 
                      color="success" 
                      size="small" 
                    />
                  )}
                </Box> */}
                {/* <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16
                  }}
                >
                  <Chip 
                    label={boutique.specialties?.[0] || 'Général'} 
                    color="secondary" 
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                  />
                </Box> */}
              </Box>

              <CardContent 
               onClick={() => navigate(`/boutiques/${boutique.id}`)}
              sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column',cursor: 'pointer', }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <Store />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {boutique.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Rating 
                        value={boutique.averageRating || 0} 
                        precision={0.1} 
                        size="small" 
                        readOnly 
                      />
                      <Typography variant="body2" color="text.secondary">
                        ({boutique.totalReviews || 0} avis)
                      </Typography>
                    </Stack>
                  </Box>
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  sx={{ 
                    flexGrow: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {boutique.description || 'Aucune description disponible'}
                </Typography>

                <Stack spacing={1} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2">{boutique.address}</Typography>
                  </Box>
                  {boutique.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Phone sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">{boutique.phone}</Typography>
                    </Box>
                  )}
                  {boutique.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Email sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">{boutique.email}</Typography>
                    </Box>
                  )}
                  {boutique.openingHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Schedule sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">{boutique.openingHours}</Typography>
                    </Box>
                  )}
                </Stack>

                <Box sx={{ mt: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {boutique.products?.length || 0} produits
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {/* <Button 
                      variant="outlined" 
                      size="small" 
                      fullWidth
                      onClick={() => navigate(`/boutiques/${boutique.id}`)}
                    >
                      Voir produits
                    </Button> */}
                    <Button 
                      variant="contained" 
                      size="small" 
                      sx={{ background: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)',p:1,}}
                      fullWidth
                      onClick={() => navigate(`/boutiques/${boutique.id}`)}
                    >
                      Découvrir la boutique
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}

      {!loading && filteredBoutiques.length === 0 && (
        <Box textAlign="center" sx={{ py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Aucune boutique trouvée
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Essayez de modifier vos critères de recherche
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mt: 4, 
          mb: 2 
        }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default Boutiques;
